import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { useCreateStripePlanMutation, useGetStripePlanDetailQuery, useUpdateStripePlanMutation } from "../../service/stripe/api";
import { usePermission } from "../../hooks/usePermission";
import { toast } from "react-toastify";

const AddEditPackage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isAddUser = location.pathname === "/manage-packages/add";

  const { hasPermission } = usePermission();
  const hasAddPermission = hasPermission("Manage Packages", "isAdd");
  const hasEditPermission = hasPermission("Manage Packages", "isEdit");
  const canSave = isAddUser ? hasAddPermission : hasEditPermission;

  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [interval, setInterval] = useState("month");
  const [features, setFeatures] = useState<string[]>([""]);

  const { data: planDetail, isSuccess } = useGetStripePlanDetailQuery(id, { skip: !id });
  const [createStripePlan, { isLoading: isCreating }] = useCreateStripePlanMutation();
  const [updateStripePlan, { isLoading: isUpdating }] = useUpdateStripePlanMutation();

  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (id && isSuccess && planDetail?.data) {
      const plan = planDetail.data;
      setName(plan.name);
      setAmount(plan.amount.toString());
      setInterval(plan.interval);
      setFeatures(plan.features.length > 0 ? plan.features : [""]);
    }
  }, [id, isSuccess, planDetail]);

  const handleFeatureChange = (index: number, value: string) => {
    const updated = [...features];
    updated[index] = value;
    setFeatures(updated);
  };

  const addFeature = () => {
    setFeatures([...features, ""]);
  };

  const removeFeature = (index: number) => {
    const updated = features.filter((_, i) => i !== index);
    setFeatures(updated);
  };

  const handleSave = async () => {
    if (!name || !amount || !interval) {
      toast.error("Please fill all required fields / يرجى تعبئة جميع الحقول المطلوبة");
      return;
    }

    try {
      const payload = {
        name,
        amount: Number(amount),
        interval,
        features: features.filter((f) => f.trim() !== ""),
      };

      if (id) {
        await updateStripePlan({ id, ...payload }).unwrap();
        toast.success("Plan updated successfully / تم تحديث الباقة بنجاح", { autoClose: 2000 });
      } else {
        await createStripePlan(payload).unwrap();
        toast.success("Plan created successfully / تم إنشاء الباقة بنجاح", { autoClose: 2000 });
      }

      setTimeout(() => {
        navigate("/manage-packages");
      }, 2000);
    } catch (error: any) {
      toast.error(error?.data?.message || (id ? "Failed to update plan / فشل تحديث الباقة" : "Failed to create plan / فشل إنشاء الباقة"));
    }
  };

  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title={isAddUser ? "Add Package / إضافة باقة" : "Edit Package / تعديل باقة"}
        breadcrumbItems={[
          { label: "Dashboard / لوحة المعلومات", onClick: () => navigate("/dashboard") },
          { label: "Manage Packages / إدارة الباقات", onClick: () => navigate("/manage-packages") },
          { label: isAddUser ? "Add Package / إضافة باقة" : "Edit Package / تعديل باقة" },
        ]}
      />

      <div className="form">
        <div className="cards">
          <div className="gap_p">
            {/* Package Name */}
            <div className="control_group w_50">
              <label>Package Name / اسم الباقة</label>
              <TextField
                placeholder="Enter Package Name / أدخل اسم الباقة"
                hiddenLabel
                fullWidth
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Package Price */}
            <div className="control_group w_33">
              <label>Price / السعر</label>
              <TextField
                type="number"
                placeholder="Enter Price / أدخل السعر"
                hiddenLabel
                fullWidth
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>

            {/* Interval */}
            <div className="control_group w_33">
              <label>Interval / المدة</label>
              <Select
                fullWidth
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
              >
                <MenuItem value="month">Month / شهر</MenuItem>
                <MenuItem value="year">Year / سنة</MenuItem>
              </Select>
            </div>

            {/* Features */}
            <div className="control_group w_50">
              <label>Features / الميزات</label>

              <div className="feature_list">
                {features.map((feature, index) => (
                  <div key={index} className="feature_item">
                    <TextField
                      placeholder={`Feature ${index + 1} / الميزة ${index + 1}`}
                      value={feature}
                      onChange={(e) =>
                        handleFeatureChange(index, e.target.value)
                      }
                      hiddenLabel
                      fullWidth
                    />

                    {features.length > 1 && (
                      <IconButton
                        className="btn_rounded red"
                        onClick={() => removeFeature(index)}
                        disabled={!canSave}
                      >
                        <CloseIcon />
                      </IconButton>
                    )}
                  </div>
                ))}

                <Button className="btn btn_primary sm" onClick={addFeature} disabled={!canSave}>
                  <AddIcon /> Add Feature / إضافة ميزة
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* SAVE BUTTON */}
        <div className="form_btn">
          <Button
            className="btn btn_primary"
            onClick={handleSave}
            disabled={isLoading || !canSave}
          >
            {isLoading ? "Saving... / جارٍ الحفظ..." : "Save / حفظ"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default AddEditPackage;
